import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {RequestV2StatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {
  RequestV2SubmissionFileStateQuery,
  RequestV2SubmissionFileStateQueryVariables,
} from 'processes/requests_v2/request-v2-submission/__generated/RequestV2SubmissionFileStateQuery';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import RequestV2SubmissionDocumentTabState from './RequestV2SubmissionDocumentTabState';
import RequestV2SubmissionProgressAndOutputsTabState from './RequestV2SubmissionProgressAndOutputsTabState';

type RequestV2SubmissionFileStateGraphQL = ExtractByTypename<
  RequestV2SubmissionFileStateQuery['requestV2Submission'],
  'RequestV2SubmissionNode'
>;

export const REQUEST_V2_SUBMISSION_FILE_STATE_QUERY: DocumentNode = gql`
  query RequestV2SubmissionFileStateQuery($fileId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    requestV2Submission(id: $fileId) {
      ... on RequestV2SubmissionNode {
        id
        name
        status
        ...BaseFolderItemFileGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class RequestV2Submission extends ApolloFolderItemBase<
  RequestV2SubmissionFileStateQuery,
  RequestV2SubmissionFileStateGraphQL
> {
  protected getQueryWrapper(): QueryWrapper<
    RequestV2SubmissionFileStateQuery,
    RequestV2SubmissionFileStateQueryVariables
  > {
    return {query: REQUEST_V2_SUBMISSION_FILE_STATE_QUERY};
  }

  protected queryVariables(): RequestV2SubmissionFileStateQueryVariables {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: RequestV2SubmissionFileStateQuery
  ): RequestV2SubmissionFileStateGraphQL | InaccessibleInfoGraphQL {
    return data.requestV2Submission;
  }

  protected getUpdatedQueryData(
    attrs: Partial<RequestV2SubmissionFileStateGraphQL>
  ): RequestV2SubmissionFileStateQuery {
    return {
      requestV2Submission: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }
}

interface RequestV2SubmissionFileState extends FolderItemState {
  model(): RequestV2Submission;
}

class RequestV2SubmissionFileState extends FolderItemState {
  static Model = RequestV2Submission;
  static fileType = ModelTypeConstants.REQUEST_V2_SUBMISSION;

  static initClass(): void {
    this.prototype.hasNote = false;
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  hasMetadata(): boolean {
    return false;
  }

  addProgressAndOutputsTab(): void {
    const requestV2SubmissionProgressAndOutputsTab = new RequestV2SubmissionProgressAndOutputsTabState({
      requestV2SubmissionFileState: this,
    });
    this.buffers().at(0).tabs().add(requestV2SubmissionProgressAndOutputsTab);

    const leftBuffer = this.buffers().at(0);
    const requestV2SubmissionDocumentTab = this.buffers().at(0).tabs().at(0);
    this.buffers().moveTab(requestV2SubmissionDocumentTab, leftBuffer, 1);
  }

  initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);
    const folderItem = this.model().get('folderItem');
    // If folderItem is undefined then folderItem is inaccessible - this should not occur due to Benchling's
    // current permission safeguards, however this check will maintain safety if there are any future developments to this.
    if (folderItem === undefined) {
      return;
    }
    let requestV2SubmissionProgressAndOutputsTab;
    if (folderItem.status !== RequestV2StatusType.DRAFT) {
      requestV2SubmissionProgressAndOutputsTab = new RequestV2SubmissionProgressAndOutputsTabState({
        requestV2SubmissionFileState: this,
      });
      this.buffers().at(0).tabs().add(requestV2SubmissionProgressAndOutputsTab);
    }

    const requestV2SubmissionDocumentTab = new RequestV2SubmissionDocumentTabState({
      id: this.id,
      requestV2SubmissionFileState: this,
    });
    this.buffers().at(0).tabs().add(requestV2SubmissionDocumentTab);
    this.activateTab(
      requestV2SubmissionProgressAndOutputsTab
        ? RequestV2SubmissionProgressAndOutputsTabState
        : RequestV2SubmissionDocumentTabState
    );
    this.set('statusbar', new statusbar.SingleBufferStatusBarState());
  }
}

RequestV2SubmissionFileState.initClass();

export default RequestV2SubmissionFileState;
